import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import DialogConfirmCancel from '../../../../dialog/DialogConfirm';
import useLearningContinuum from '../../../../../hooks/learning-continuum/useLearningContinuum';
import { useParams } from 'react-router-dom';
import { Box, FormControlLabel, Switch } from '@mui/material';
import { useSelector } from 'react-redux';
import SelectField from '../../../../FormControl/input/SelectField';
import IconTooltipButton from '../../../../button/IconTooltipButton';

const DialogExport = ({}) => {
  const exportTypes = ['xlsx', 'csv'];
  const { curriculumId } = useParams();

  const columnsTable = useSelector((state) => state?.datatable?.lc?.[curriculumId] || []);
  const state = useSelector((state) => state?.filter?.lc);

  const [loading, toggleLoading] = useState(false);
  const [errors, setInputErrors] = useState({});
  const [modal, setModal] = useState(null);
  const [form, setInputForm] = useState({
    exportType: 'xlsx'
  });
  const { getLearningContinuaForExport } = useLearningContinuum();

  const onChangeColumnToExport = (key, value) => {
    const newColumns = form.columns.map((column) => {
      if (column.key === key) {
        return { ...column, export: value };
      }
      return column;
    });
    setInputForm({ ...form, columns: newColumns });
  };

  const onChange = (key, value) => {
    setInputForm({ ...form, [key]: value });
  };
  const onConfirm = async (e) => {
    if (!exportTypes.includes(form.exportType)) {
      setInputErrors({ message: 'Invalid type' });
      return;
    }

    e.preventDefault();
    toggleLoading(true);
    try {
      let result = await getLearningContinuaForExport(
        curriculumId,
        form.exportType,
        state.filter || {},
        form.columns
      );
      if (result.error) {
        setInputErrors(result);
      } else {
        onClose(false);
      }
    } finally {
      toggleLoading(false);
    }
  };
  const onClose = () => {
    setModal(false);
    onLoadColumns();
  };
  const onLoadColumns = useCallback(() => {
    const columnToExport = columnsTable.map((col) => ({
      key: col.key,
      label: `${col.type || ''}_${col.label}`,
      export: col.show
    }));
    columnToExport.push({
      key: 'levels',
      label: 'levels',
      export: false
    });
    setInputForm({ ...form, columns: columnToExport });
  }, [columnsTable]);

  useEffect(() => {
    onLoadColumns();
  }, [onLoadColumns]);

  return (
    <>
      <IconTooltipButton
        disabled={loading}
        title='Export Learning Continuum'
        icon='file-export'
        onClick={() => setModal(true)}
        startIcon={'file-export'}
        style={{
          borderRadius: '0.5rem',
          backgroundColor: '#008c3a',
          color: '#e6f4ea',
          padding: '0.45rem',
          opacity: loading ? 0.5 : 1
        }}
      />

      {modal && (
        <DialogConfirmCancel
          id={`learning-continuum-${form?._id}`}
          open={modal}
          onCancel={onClose}
          onConfirm={onConfirm}
          loading={loading}
          title={'Export Learning Continuum'}
          onCloseHeader={true}
          maxWidth='xs'
        >
          <Box className='grid grid-cols-3 gap-4 mb-4 justify-start items-center'>
            <Typography variant='inherit' color='primary' className='text-center font-bold'>
              Export Type
            </Typography>
            <div className='col-span-2'>
              <SelectField
                name='exportType'
                value={form.exportType}
                onChange={(e) => onChange('exportType', e.target.value)}
                options={exportTypes.map((type) => ({
                  label: `.${type.toUpperCase()}`,
                  value: type
                }))}
              />
            </div>
          </Box>
          <Box className='bg-primary text-primary-foreground px-4 py-1 flex gap-4 items-center justify-between sticky top-0 z-10 h-8'>
            <Typography variant='subtitle1' className='uppercase'>
              Columns to Export
            </Typography>
          </Box>
          <Box className='flex flex-col gap-1 pr-4'>
            {form.columns?.map((column) => (
              <FormControlLabel
                key={column.key}
                value={column.key}
                name={column.key}
                className='justify-between'
                control={
                  <Switch
                    color='primary'
                    checked={column.export}
                    onChange={(e) => onChangeColumnToExport(column.key, e.target.checked)}
                  />
                }
                label={
                  <Typography variant='inherit' color='primary' className='uppercase font-bold'>
                    {column.label}
                  </Typography>
                }
                labelPlacement='start'
              />
            ))}
          </Box>

          {errors?.message && <Alert severity='error'>{errors?.message}</Alert>}
        </DialogConfirmCancel>
      )}
    </>
  );
};

export default DialogExport;
